import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { fetchEligibilityDetail } from "../../api/eligibilities";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import StatusBadge from "../../components/admin/StatusBadge";

export const DetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();

  const { data, isLoading, error } = useQuery({
    queryKey: ["eligibility-detail", id],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return fetchEligibilityDetail(token, id!);
    },
    enabled: !!id,
  });

  const columns = useMemo(
    () => [
      {
        accessorKey: "firstName",
        header: "First Name",
        cell: (info: any) => (
          <span className="block truncate">
            {info.getValue()}
          </span>
        ),
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
        cell: (info: any) => (
          <span className="block truncate">
            {info.getValue()}
          </span>
        ),
      },
      {
        accessorKey: "email",
        header: "Email",
      },
      {
        id: "address",
        header: "Address",
        cell: (info: any) => {
          const address = info.row.original.address || {};
          const address1 = address.address1 || "";
          const city = address.city || "";
          const state = address.state || "";
          const parts = [address1, city, state].filter(Boolean);
          return parts.length > 0 ? parts.join(", ") : "-";
        },
      },
      {
        accessorKey: "subscriptionExpirationDate",
        header: "Subscription End Date",
        cell: (info: any) => {
            console.log(info)
          const value = info.getValue();
          if (!value) return "-";
          const date = new Date(value);
          return isNaN(date.getTime()) ? "-" : date.toLocaleDateString();
        },
      },
      {
        accessorKey: "organizationName",
        header: "Organization",
        cell: (info: any) => info.getValue() || "-",
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (info: any) => (
          <StatusBadge status={info.getValue()} message={info.row.original.message} />
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: data?.results || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg
          className="animate-spin h-8 w-8 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8z"
          />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 py-8 text-center">
        Error loading eligibility detail.
      </div>
    );
  }

  if (!data) {
    return <div className="text-center py-8">No data found.</div>;
  }

  return (
    <div className="p-6 bg-white rounded shadow">
      <div className="flex items-center justify-between mb-4">
        {data.fileName ? (
          <>
            <div>
              <span className="font-bold">Eligibility details for </span>
              <code className="font-mono bg-gray-100 px-2 py-1 rounded text-base font-normal">{data.fileName}</code>
            </div>
            {data.status ? (
              <StatusBadge status={data.status} />
            ) : null}
          </>
        ) : (
          <span className="font-bold text-xl">Eligibility Upload Details</span>
        )}
      </div>
      <div className="overflow-x-auto rounded">
        <table className="min-w-full border border-gray-200">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={"px-4 py-2 text-left text-xs font-semibold text-gray-700 border-b"}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-8 text-gray-500"
                >
                  No results found.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={"px-4 py-2 border-b"}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end mt-4">
        <button
          className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition"
          onClick={() => navigate("/admin/eligibilities/history")}
        >
          &larr; Back to History
        </button>
      </div>
    </div>
  );
};
